/**
 * cross-cp-prompts.ts — Out-of-order capture utility (M-009 v1)
 *
 * Detects when a rep's typed evidence text mentions topics associated with
 * a CP other than the one currently being answered, and returns prompts
 * suggesting the rep also log evidence against that CP.
 *
 * Usage:
 *   import { detectCrossCpMentions } from "@/lib/cross-cp-prompts";
 *   const matches = detectCrossCpMentions(notesText, 5);
 *   // => [{ cp: 9, keyword: "procurement" }]
 *
 * Design note (M-009 v1):
 *   The "Log against CP{N}" button in AnswerEntryModal and QaPanel fires a
 *   toast for now ("navigate to that CP manually"). Full deep-link open of a
 *   second AnswerEntryModal pre-targeted at the most relevant question inside
 *   that CP is deferred to v1.1 — implementing it cleanly requires a shared
 *   modal-stack context that does not yet exist. The toast path is simpler and
 *   ships today without architectural debt.
 */

// ─── Keyword map ─────────────────────────────────────────────────────────────
// Each entry lists the CP number and the keywords associated with that CP.
// Keywords are matched case-insensitively against the full evidence text.

export interface CpKeywordEntry {
  cp: number;
  label: string;
  keywords: string[];
}

export const CP_KEYWORD_MAP: CpKeywordEntry[] = [
  {
    cp: 3,
    label: "Pain / Problem",
    keywords: [
      "pain",
      "broken",
      "losing",
      "manual",
      "spreadsheet",
    ],
  },
  {
    cp: 4,
    label: "Pain / Fit",
    keywords: [
      "demo",
      "showed",
      "POC",
      "pilot",
    ],
  },
  {
    cp: 5,
    label: "Budget",
    keywords: [
      "budget",
      "spend",
      "approved",
      "funded",
      "cost center",
    ],
  },
  {
    cp: 6,
    label: "Timeline",
    keywords: [
      "Q1",
      "Q2",
      "Q3",
      "Q4",
      "by end of",
      "deadline",
      "go-live",
    ],
  },
  {
    cp: 7,
    label: "Champion",
    keywords: [
      "champion",
      "sponsor",
      "advocate",
      "internal",
      "selling for us",
    ],
  },
  {
    cp: 8,
    label: "Stakeholder Map",
    keywords: [
      "VP",
      "CFO",
      "CTO",
      "CIO",
      "head of",
      "decision committee",
    ],
  },
  {
    cp: 9,
    label: "Paper / Procurement",
    keywords: [
      "procurement",
      "legal",
      "redline",
      "contract",
      "MSA",
      "NDA",
      "security review",
      "IT review",
    ],
  },
  {
    cp: 10,
    label: "Implementation",
    keywords: [
      "implementation",
      "go-live",
      "training",
      "rollout",
      "onboarding",
      "change management",
      "ROI",
    ],
  },
];

// ─── Detection logic ──────────────────────────────────────────────────────────

export interface CrossCpMatch {
  cp: number;
  cpLabel: string;
  keyword: string;
}

/**
 * Scans `text` for keywords associated with CPs other than `currentCp`.
 * Returns one match per CP (the first keyword found), sorted by CP number.
 * Returns an empty array if no cross-CP mentions are detected.
 */
export function detectCrossCpMentions(
  text: string,
  currentCp: number,
): CrossCpMatch[] {
  if (!text || !text.trim()) return [];

  const matches: CrossCpMatch[] = [];
  const lowerText = text.toLowerCase();

  for (const entry of CP_KEYWORD_MAP) {
    if (entry.cp === currentCp) continue;

    for (const keyword of entry.keywords) {
      // Case-insensitive whole-token match.
      // We use a word-boundary-aware check: the keyword must appear as a
      // standalone token or phrase (not just a substring of another word).
      // For multi-word phrases we do a simple includes() — acceptable for v1.
      const isPhrase = keyword.includes(" ");
      let found = false;

      if (isPhrase) {
        found = lowerText.includes(keyword.toLowerCase());
      } else {
        // Single-word: require word boundary on both sides using regex.
        // Escape any regex special chars in the keyword first.
        const escaped = keyword.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
        const re = new RegExp(`(?<![a-zA-Z0-9])${escaped}(?![a-zA-Z0-9])`, "i");
        found = re.test(text);
      }

      if (found) {
        matches.push({ cp: entry.cp, cpLabel: entry.cpLabel, keyword });
        break; // One match per CP is sufficient for the prompt
      }
    }
  }

  return matches.sort((a, b) => a.cp - b.cp);
}
